// Multi-selectable photo grid for the desktop book editor (VerticalGridDynamic
// subclass). Adds per-photo and per-date select-circles.
// Selection lives on models.Photo.selected (reactive) so it survives virtualisation ;
// groups are read from this._structure (label rows have .item, photo rows have .items).
fx.ns("shared.components", class SelectablePhotoGrid extends fx.components.VerticalGridDynamic {

	constructor(parameters)
	{
		super(parameters);

		// date labels are plain objects -> "Object" renderer key
		this._renderers = Object.assign({}, this._renderers, { Object : shared.renderers.PhotoDateLabelRenderer });

		// let the renderers' circles call back into the grid
		if (!this._sharedData)
			this._sharedData = {};
		this._sharedData.owner = this;
	}

	// Date-label items are injected without an id, so every label row collides under the same
	// `undefined` key in the grid's _currentRenderers (only one date header/checkbox renders).
	// Give each label row a stable unique id after the base builds the structure.
	_estimateStructure()
	{
		super._estimateStructure();

		if (this._structure)
			for (let i = 0; i < this._structure.length; i++)
			{
				let row = this._structure[i];
				if (row.item && (row.item.id === undefined || row.item.id === null))
					row.item.id = "date-label-" + i;
			}
	}

	// push the Photo models of a structure row into `out`
	_collectPhotos(row, out)
	{
		if (!row.items) return;

		for (let j = 0; j < row.items.length; j++)
		{
			let el = row.items[j].element;
			if (el && !el.deleted && el instanceof models.Photo)
				out.push(el);
		}
	}

	// all photo models in the structure (every day, not just visible)
	_allPhotos()
	{
		let photos = [];
		if (!this._structure) return photos;

		for (let i = 0; i < this._structure.length; i++)
			this._collectPhotos(this._structure[i], photos);

		return photos;
	}

	get selectedPhotos()
	{
		return this._allPhotos().filter(function(p){ return p.selected; });
	}

	_indexOfLabel(labelElement)
	{
		if (!this._structure) return -1;

		for (let i = 0; i < this._structure.length; i++)
		{
			let row = this._structure[i];
			if (row.item && row.item.element === labelElement)
				return i;
		}

		return -1;
	}

	// photos of the day whose label row is at structure index `index`
	_groupPhotosAt(index)
	{
		let photos = [];
		if (!this._structure) return photos;

		for (let i = index + 1; i < this._structure.length; i++)
		{
			let row = this._structure[i];
			if (row.item) break; // next date label -> end of day

			this._collectPhotos(row, photos);
		}

		return photos;
	}

	_groupPhotosForLabel(labelElement)
	{
		let index = this._indexOfLabel(labelElement);
		return index == -1 ? [] : this._groupPhotosAt(index);
	}

	get maxSelection()
	{
		return 10;   // matches the max photos placeable on a page (BookBoard drop cap)
	}

	selectedCount()
	{
		let photos = this._allPhotos();
		let n = 0;
		for (let i = 0; i < photos.length; i++)
			if (photos[i].selected)
				n++;
		return n;
	}

	// toggle one photo, enforcing the selection cap (deselecting is always allowed)
	toggleSelection(photo)
	{
		if (!photo) return;

		if (photo.selected)
			photo.selected = false;
		else if (this.selectedCount() < this.maxSelection)
			photo.selected = true;
		else
			return;   // at the cap : ignore new selections

		this.onSelectionChanged(photo);
	}

	isDateGroupSelected(labelElement)
	{
		let photos = this._groupPhotosForLabel(labelElement);
		return photos.length > 0 && photos.every(function(p){ return p.selected; });
	}

	toggleDateGroup(labelElement)
	{
		let photos = this._groupPhotosForLabel(labelElement);
		if (photos.length == 0) return;

		let last;
		if (photos.some(function(p){ return p.selected; }))
		{
			// any photo of the day selected -> clear the whole day
			for (let i = 0; i < photos.length; i++)
				photos[i].selected = false;
		}
		else
		{
			// none selected -> select the first photos, up to the remaining capacity
			let remaining = this.maxSelection - this.selectedCount();
			for (let i = 0; i < photos.length && remaining > 0; i++)
			{
				photos[i].selected = true;
				last = photos[i];
				remaining--;
			}
		}

		this.onSelectionChanged(last);
	}

	onSelectionChanged(photo)
	{
		this._syncDateCircles();

		let count = this.selectedCount();
		this.toggleClass("has-selection", count > 0);
		this.toggleClass("selection-full", count >= this.maxSelection);

		// `photo` = the photo that triggered this change (undefined on clear/deselect),
		// used by the panel to anchor its one-time hints.
		this.trigger("selection-count-changed", { count : count, photo : photo });
	}

	// re-check/uncheck each visible date circle from its photos' state
	_syncDateCircles()
	{
		if (!this._structure) return;

		for (let i = 0; i < this._structure.length; i++)
		{
			let row = this._structure[i];
			if (!row.item) continue; // not a label

			let renderer = this._currentRenderers[row.item.id];
			if (!renderer || !renderer.setChecked) continue;

			let photos = this._groupPhotosAt(i);
			let all = photos.length > 0 && photos.every(function(p){ return p.selected; });
			renderer.setChecked(all);
		}
	}
});
